<script setup>
import { ref, onMounted, onUnmounted, computed, watch, defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from '@/composables/use-toast';
import { useStorage } from '@/composables/use-storage';
import { uploadModelResponseFile, createNewEvalRun, downloadTaskBundlesFile } from '@/services/evalService';
import { getActivePaymentMethods } from '@/services/paymentMethodsService';
import StripeLogo from '@/assets/images/stripe.png';
import USDCLogo from '@/assets/images/usdc.png';
import StripePayment from './StripePayment.vue';
import PortexLogo from '@/assets/images/avatar/orb_avatar.png';
import CountryStep from './CountryStep.vue';
import Alert from '@/components/Alert.vue';
import Checkbox from '@/components/checkbox.vue';
import axios from 'axios';
import USDCPayment from './USDCPayment.vue';
import SelectInput from '../forms/inputs/select-input.vue';

const router = useRouter();

const { addToast, removeToast } = useToast();
const { hasFlag, getUserInfo } = useStorage();
const userInfo = ref(getUserInfo());

const props = defineProps({
    showModal: {
        type: Boolean,
        required: true
    },
    taskBundles: {
        type: Object,
        required: true
    },
    evalListing: {
        type: Object,
        required: true
    },
    versions: {
        type: Array,
        required: false,
        default: () => []
    },
});

const emit = defineEmits(['update:showModal', 'eval-completed']);

const isLoading = ref(false);
const uploadedFile = ref(null);
const uploadedFilePath = ref(null);
const uploadStatus = ref(null);
const uploadProgress = ref(0);
const isProcessingStripePayment = ref(false);
const isProcessingUSDCPayment = ref(false);
const isProcessingCreditsPayment = ref(false);
const isPaymentFinished = ref(false);
const fileInputRef = ref(null);
const showStripePayment = ref(false);
const payments = ref([]);
const userCountry = ref({
    country: userInfo.value.country,
    postalCode: userInfo.value.postal_code
});
const showCloseAlert = ref(false);
const amount = ref(props.evalListing.eval_price);
const useCredits = ref(false);
const creditsBalance = ref(0);
const creditsUsed = ref(0);
const isModalInitialized = ref(false);
const calculateTotalPrice = ref(0)
const currentEvalRun = ref(null);
const availablePayments = ref([]);

const isFileUploaded = computed(() => uploadStatus.value === 'uploaded');
const hasUploadedFile = computed(() => uploadedFile.value !== null);

const handleClose = async () => {
    isProcessingStripePayment.value = false;
    isProcessingUSDCPayment.value = false;
    isProcessingCreditsPayment.value = false;
    
    if (hasUploadedFile.value && !isPaymentFinished.value) {
        showCloseAlert.value = true;
        return;
    }
    
    await resetModal({ removeUploadedFile: !isPaymentFinished.value });
    emit('update:showModal', false);
};

const handleCloseConfirm = async () => {
    showCloseAlert.value = false;
    await resetModal();
    emit('update:showModal', false);
};

const handleCloseCancel = () => {
    showCloseAlert.value = false;
};

const resetModal = async ({ removeUploadedFile: shouldRemoveUploadedFile = true } = {}) => {
    if (shouldRemoveUploadedFile) {
        await removeUploadedFile();
    } else {
        uploadedFile.value = null;
        uploadedFilePath.value = null;
        uploadStatus.value = null;
        uploadProgress.value = 0;
        fileVersion.value = null;
        isVersionLocked.value = false;
        if (fileInputRef.value) {
            fileInputRef.value.value = '';
        }
    }
    currentEvalRun.value = null;
    isProcessingStripePayment.value = false;
    isProcessingUSDCPayment.value = false;
    isProcessingCreditsPayment.value = false;
    isPaymentFinished.value = false;
    isLoading.value = false;
    showStripePayment.value = false;
    payments.value = [];
    availablePayments.value = [];
    showCloseAlert.value = false;
};

const triggerFileUpload = () => {
    fileInputRef.value?.click();
};

const fileVersion = ref(null);
const isVersionLocked = ref(false);

const validateFile = (file) => {
       if (!file || typeof file !== 'object' || Array.isArray(file)) {
           return { valid: false, error: 'Model response must be an object with version and prompts' };
       }

       const { version, prompts } = file;
       const versionNumber = Number(version);
       if (!Number.isFinite(versionNumber)) {
           return { valid: false, error: 'Model response file must include a valid version' };
       }

       if (!Array.isArray(prompts)) {
           return { valid: false, error: 'Model response prompts must be an array' };
       }

       for (let i = 0; i < prompts.length; i++) {
           const entry = prompts[i];

           if (!entry || typeof entry !== 'object') {
               return { valid: false, error: `Entry at index ${i} must be an object` };
           }

           if (entry.task_id === undefined || entry.task_id === null) {
               return { valid: false, error: `Entry at index ${i} is missing required field: task_id` };
           }

           if (entry.model_response === undefined || entry.model_response === null) {
               return { valid: false, error: `Entry at index ${i} is missing required field: model_response` };
           }
       }

       return { valid: true, version: versionNumber };
   }


const handleFileSelect = async (event) => {


    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
        addToast('Please upload a JSON file', 'error');
        event.target.value = '';
        return;
    }

    try {
        const text = await file.text();
        const validation = validateFile(JSON.parse(text));
        if (!validation.valid) {
            event.target.value = '';
            throw new Error(validation.error);
        }
        if (validation.version != null) {
            updateVersions();
            fileVersion.value = validation.version;
            const versionExists = versionsOptions.value.some(option => Number(option.value) === Number(fileVersion.value));
            if (!versionExists) {
                event.target.value = '';
                throw new Error('Version from model response does not exist in this dataset');
            }
            selectedVersionNumber.value = validation.version;
            isVersionLocked.value = true;
        }
    } catch (error) {
        console.error(error);
        addToast(error.message, 'error');
        event.target.value = '';
        return;
    }

    uploadedFile.value = file;
    uploadStatus.value = 'uploading';
    uploadProgress.value = 0;

    try {
        isLoading.value = true;
        const progressInterval = setInterval(() => {
            uploadProgress.value += 10;
            if (uploadProgress.value >= 90) {
                clearInterval(progressInterval);
            }
        }, 100);

        const response = await uploadModelResponseFile(props.evalListing.datasets[0].dataset_id, file, fileVersion.value ?? null);

        clearInterval(progressInterval);
        uploadProgress.value = 100;
        
        if (response.status === 200 || response.status === 201) {
            uploadStatus.value = 'uploaded';
            uploadedFilePath.value = response.data.model_response_file;
            addToast('File uploaded successfully', 'success');
        } else {
            throw new Error('Upload failed');
        }
    } catch (error) {
        await removeUploadedFile({ silent: true });
        uploadStatus.value = 'error';
        uploadProgress.value = 0;
        addToast(error.response?.data?.message || 'Failed to upload file', 'error');
    } finally {
        isLoading.value = false;
    }
};

const createPayment = async (amountPayment, tx) => {
    if(tx){
        payments.value.push({
            id: availablePayments.value.find(p => p.name === 'wallet')?.id,
            method: 'wallet',
            amount: amountPayment
        });
    }
    await createEvalRun(amountPayment, tx);
}

const handlePayWithStripe = () => {
    showStripePayment.value = true;
    payments.value.push({
        id: availablePayments.value.find(p => p.name === 'stripe')?.id,
        method: 'stripe',
        amount: calculateTotalPrice.value
    });
}

const handleConnectStripe = () => {
    router.push('/stripe/connect');
}

const handlePaymentCredits = async () => {
    isProcessingCreditsPayment.value = true;
    const total = payments.value.reduce((acc, payment) => acc + payment.amount, 0);
    await createPayment(total.toString(), null);
}

const goBackToMain = () => {
    showStripePayment.value = false;
    showUSDCPayment.value = false;
    payments.value.pop();
};

const handleUserCountry = (value) => {
    userCountry.value = value;
};

const handleUseCredits = async () => {
    if(props.evalListing === null) return;
    let price = Number.parseFloat(amount.value);
    let used = 0;
    const credits = Number.parseFloat(creditsBalance.value);
    if(useCredits.value && credits >= price) {
        used = price;
        price = 0;
    }
    if(useCredits.value && credits < price) {
        used = credits;
        price = price - credits;
    }
    creditsUsed.value = used;
    
    payments.value = payments.value.filter(p => p.method !== 'credits');
    
    if(used > 0) {
        payments.value.push({
            id: availablePayments.value.find(p => p.name === 'credits')?.id,
            method: 'credits',
            amount: used
        });
    }
    
    calculateTotalPrice.value = price;
}

const fetchCredits = async () => {
    const response = await axios.get('/api/users/credits')
    creditsBalance.value = response.data;
}

const canUseCredits = computed(() => {
    return props.evalListing.accept_credits && creditsBalance.value > 0 && !props.evalListing.applicant_id;
})

watch(useCredits, async () => {
    if (isModalInitialized.value) {
        await handleUseCredits();
    }
});

const paymentMethods = ref([]);

const feeAmout = ref(0);
const versionsOptions = ref([]);
const lastVersion = ref(null);
const selectedVersionNumber = ref(null);
const countVersions = ref(0);
const resolveVersions = () => {
    const fromProps = Array.isArray(props.versions) && props.versions.length > 0 ? props.versions : null;
    const fromListing = props.evalListing?.datasets?.[0]?.dataset?.versions;
    const versions = fromProps ?? fromListing;
    return Array.isArray(versions) ? versions : [];
};

const updateVersions = () => {
    const versions = resolveVersions();
    versionsOptions.value = versions.map(version => ({
        value: Number(version.version),
        label: `v${version.version}`,
    }));
    countVersions.value = versions.length;
    if (versions.length === 0) {
        lastVersion.value = null;
        if (!isVersionLocked.value) {
            selectedVersionNumber.value = null;
        }
        return;
    }
    lastVersion.value = versions.reduce((a, b) => Number(a.version) > Number(b.version) ? a : b);
    if (!isVersionLocked.value) {
        const selectedExists = versionsOptions.value.some(option => Number(option.value) === Number(selectedVersionNumber.value));
        if (!selectedExists) {
            selectedVersionNumber.value = Number(lastVersion.value?.version);
        }
    }
};

const hasVersions = computed(() => countVersions.value >= 1);

watch(() => props.versions, () => {
    updateVersions();
}, { deep: true });

watch(() => props.evalListing?.datasets?.[0]?.dataset?.versions, () => {
    updateVersions();
}, { deep: true });

onMounted(async () => {
    updateVersions();
    availablePayments.value = await getActivePaymentMethods();
    const sellerStripeAccount = props.evalListing?.datasets[0]?.dataset?.seller?.stripe_account_id;
    if (props.evalListing?.payment_methods?.length > 0 && props.evalListing.payment_methods?.filter(method => method === availablePayments.value.find(p => p.name === 'wallet')?.id).length > 0) {
        paymentMethods.value.push({
            id: availablePayments.value.find(p => p.name === 'wallet')?.id,
            name: 'USDC',
            logo: USDCLogo,
            description: 'Save fees, requires a wallet.',
            payWith: 'Pay with USDC',
            clickAction: handleUSDCPayment,
        });
    }
    if (hasFlag('stripe-payment')) {
        if (sellerStripeAccount && props.evalListing.payment_methods?.filter(method => method === availablePayments.value.find(p => p.name === 'stripe')?.id).length > 0) {
            paymentMethods.value.push({
                id: availablePayments.value.find(p => p.name === 'stripe')?.id,
                name: 'Stripe',
                logo: StripeLogo,
                description: 'Pay with credit card',
                payWith: 'Pay with Stripe',
                clickAction: handlePayWithStripe,
            });
        }
    }
    if (calculateTotalPrice.value > 0 && useCredits.value) {
        if (props.evalListing.payment_methods?.filter(method => method === availablePayments.value.find(p => p.name === 'credits')?.id).length > 0) {
            paymentMethods.value.push({
                id: availablePayments.value.find(p => p.name === 'credits')?.id,
                name: 'Credits',
                logo: PortexLogo,
                description: 'Use credits',
                payWith: 'Pay with Credits',
                clickAction: handlePaymentCredits,
            });
        }
    }
    
    await fetchCredits();
    await handleUseCredits();
    updateVersions();
    setTimeout(() => {
        isModalInitialized.value = true;
    }, 100);
});

onUnmounted(async () => {
    await resetModal({ removeUploadedFile: !isPaymentFinished.value });
});

const handlePaymentSuccess = async (sale) => {
    if (!sale?.id) return;
    if (sale?.paid || sale?.tx_hash === 'void_tx') {
        addToast('Payment successful! Your eval run will be created shortly.', 'success');
        isPaymentFinished.value = true;
        emit('eval-completed', currentEvalRun.value ?? null);
        handleClose();
    } else {
        addToast('Purchase didn\'t work, please try again later', 'error');
    }
};
const handleUSDCPaymentSuccess = () => {
    isPaymentFinished.value = true;
};
const showUSDCPayment = ref(false);
const handleUSDCPayment = async () => {
    if (!isFileUploaded.value) return;

    showUSDCPayment.value = true;
    isLoading.value = true;
};

const createEvalRun = async (amountPayment, txHash) => {
    isLoading.value = true;
    try {
        if (!uploadedFilePath.value) {
            throw new Error('File path not found. Please upload the file again.');
        }
        const response = await createNewEvalRun({
            eval_id: props.evalListing?.datasets[0]?.dataset?.eval_id,
            dataset_id: props.evalListing?.datasets[0]?.dataset_id,
            listing_id: props.evalListing?.id,
            model_response_file: uploadedFilePath.value,
            buyer_id: userInfo.value.id,
            seller_id: props.evalListing.datasets[0].dataset.seller.user.id,
            tx_hash: txHash ? txHash.transactionHash : 'void_tx',
            receipt_address: props.evalListing.receipt_address,
            final_price: Number.parseFloat(amount.value) + Number.parseFloat(feeAmout.value),
            currency: 'USD',
            status: 'running',
            payments: payments.value,
            version: selectedVersionNumber.value
        });

        if (response?.evalRun?.sale?.paid || response?.evalRun?.sale?.tx_hash === 'void_tx')  {
            currentEvalRun.value = response.evalRun;
            handlePaymentSuccess(response.evalRun.sale);
            globalThis.location.href = `/checkout-confirmation?sale_id=${response.evalRun.sale.id}`;
        } else {
            addToast('Purchase didn\'t work, please try again later', 'error');
        }
    } catch (error) {
        console.error('Failed to create eval run:', error);
        addToast(error.response?.data?.message || 'Failed to create eval run', 'error');
    } finally {
        isLoading.value = false;
        isProcessingStripePayment.value = false;
        isProcessingUSDCPayment.value = false;
        isProcessingCreditsPayment.value = false;
        handleClose();
    }
};

const formatPrice = (price) => {
    if (!price && price !== 0) return '0 USD';
    const numPrice = Number(price);
    if (Number.isInteger(numPrice)) {
        return numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' USD';
    }
    return numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USD';
};

const handleClickOutside = async (event) => {
    if (event.target.id === 'run-eval') {
        await handleClose();
    }
};

const removeUploadedFile = async ({ silent = false } = {}) => {
    const shouldRemoveFromServer = hasUploadedFile.value && props.evalListing?.datasets?.[0]?.dataset_id;
    uploadedFile.value = null;
    uploadedFilePath.value = null;
    uploadStatus.value = null;
    uploadProgress.value = 0;
    fileVersion.value = null;
    isVersionLocked.value = false;
    if (fileInputRef.value) {
        fileInputRef.value.value = '';
    }
    if (shouldRemoveFromServer) {
        try {
            const response = await axios.post(`/api/eval-runs/${props.evalListing.datasets[0].dataset_id}/delete-model-response-file`);
            if (response.status === 200 || response.status === 201) {
                if (!silent) {
                    addToast('File removed successfully', 'info');
                }
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            if (!silent) {
                addToast(error.response?.data?.message || 'Failed to remove file', 'error');
            }
        }
    }
};

const downloadTaskBundle = async () => {
    const loadingToastId = addToast('Downloading task bundle...', 'loading');
    if(await downloadTaskBundlesFile(props.evalListing?.datasets[0]?.dataset_id)) {
        addToast('Download complete!', 'success');
    } else {
        addToast('Download failed', 'error');
    }
    removeToast(loadingToastId);
};
</script>

<template>
    <Teleport to="body">
        <div
            v-if="showModal"
            id="run-eval"
            class="z-50 overflow-hidden inset-0 m-auto flex justify-center items-center bg-echoes-background-black/50 backdrop-blur-sm"
            :class="showModal ? 'fixed' : 'hidden'"
            tabindex="-1"
            @click="handleClickOutside"
        >
            <div class="relative w-full max-w-2xl my-auto p-4 animate-slideIn">
                <div class="relative bg-echoes-background-black rounded-[21px] shadow-xl animate-slideIn overflow-hidden border border-echoes-border-gray">
                    <!-- Header -->
                    <div class="flex justify-between border-b border-echoes-border-gray items-center px-5 py-3">
                        <div class="flex items-center gap-3">
                            <button v-if="showStripePayment || showUSDCPayment" 
                                    @click="goBackToMain"
                                    class="text-white hover:text-echoes-primary rounded-lg p-1.5 inline-flex items-center transition-colors">
                                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <h5 class="text-lg text-white font-bold font-weight-700">
                                {{ showStripePayment ? 'Stripe Payment' : 'Eval Checkout' }}
                            </h5>
                        </div>
                        <button type="button"
                                @click="handleClose"
                                class="text-white bg-transparent hover:bg-white/10 hover:text-red-600 transition-colors duration-300 ease-in-out rounded-lg text-sm p-1.5 ms-auto inline-flex items-center">
                            <span class="sr-only">Close modal</span>
                            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Body with transition -->
                    <div class="relative overflow-hidden overflow-x-hidden max-h-[75vh]">
                        <Transition name="slide" mode="out-in">
                            <div :key="showStripePayment ? 'stripe' : showUSDCPayment ? 'usdc' : 'main'">
                            <!-- Main Content -->
                            <template v-if="!showStripePayment && !showUSDCPayment">
                                <div key="main" class="px-5 py-2 max-h-[calc(90vh-16vh)] overflow-y-auto space-y-4">
                                    <!-- Info Banner -->
                                    <div class="flex items-start p-3 bg-white/10 border border-white/10 rounded-lg">
                                        <div class="flex text-xs text-white/80 items-center gap-2">
                                            <div class="flex-shrink-0 flex items-center justify-center">
                                            <svg class="w-4 h-4 text-green-500/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <p>
                                            In order to run the eval, you will need to upload a JSON with your model's output as described below. 
                                            For more,  
                                            <a href="https://docs.portexai.com/portex-docs/datalab/running-an-eval" target="_blank" class="underline hover:text-primary/80 hover:text-echoes-primary">
                                                check this guide.
                                            </a>
                                        </p>
                                    </div>
                                </div>

                                
                                <!-- Eval Instructions -->
                                <div class="space-y-2">
                                    <h3 class="text-xs font-semibold font-weight-600 text-white uppercase">Eval Instructions</h3>
                                    <ol class="space-y-2 text-xs text-white/80 list-decimal list-inside pl-4">
                                        <li class="flex items-start gap-2">
                                            <span class="flex-shrink-0 flex items-center justify-center text-white text-xs">1.</span>
                                            <span>
                                                Download the Task Bundles 
                                                <button @click="downloadTaskBundle()" class="text-echoes-gray/80 hover:text-echoes-gray/80 underline hover:text-echoes-primary">
                                                    (click here)
                                                </button>
                                            </span>
                                        </li>
                                        <li class="flex items-start gap-2">
                                            <span class="flex-shrink-0 flex items-center justify-center text-white text-xs">2.</span>
                                            <span>Run the tasks through your model.</span>
                                        </li>
                                        <li class="flex items-start gap-2">
                                            <span class="flex-shrink-0 flex items-center justify-center text-white text-xs">3.</span>
                                            <span>Create a JSON with <code class="px-1 py-0.5 bg-echoes-background-black rounded text-echoes-gray/80">version</code> and <code class="px-1 py-0.5 bg-echoes-background-black rounded text-echoes-gray/80">prompts</code>, where each prompt includes <code class="px-1 py-0.5 bg-echoes-background-black rounded text-echoes-gray/80">model_response</code>.</span>
                                        </li>
                                        <li class="flex items-start gap-2">
                                            <span class="flex-shrink-0 flex items-center justify-center text-white text-xs">4.</span>
                                            <span>Upload the JSON using the button below.</span>
                                        </li>
                                        <li class="flex items-start gap-2">
                                            <span class="flex-shrink-0 flex items-center justify-center text-white text-xs">5.</span>
                                            <span>Checkout with the preferred payment method.</span>
                                        </li>
                                    </ol>
                                </div>

                                <div class="flex items-center gap-2 p-2 bg-echoes-primary/10 rounded-lg" v-if="hasVersions">
                                    <span class="text-white text-xs">Current version is: <span class="font-bold">v{{ lastVersion?.version }}</span> If you use a different version, please select: </span>
                                    <SelectInput    
                                        v-model="selectedVersionNumber"
                                        :options="versionsOptions"
                                        label="version"
                                        value="value"
                                        placeholder="Select the version"
                                        class="w-1/3"
                                        :disabled="isVersionLocked || countVersions === 1"
                                        size="sm"
                                    />
                                </div>
                                
                                <!-- Main Actions Row -->
                                <div class="space-y-2">
                                    <!-- Labels Row -->
                                    <div class="flex gap-3 items-center">
                                        <div class="flex-1 text-center">
                                            <label for="fileInputRef" class="text-xs font-semibold font-weight-600 text-white uppercase">1. Eval Upload</label>
                                        </div>
                                        
                                        <!-- Arrow Space -->
                                        <div class="px-2">
                                            <div class="w-6"></div>
                                        </div>
                                        <div class="flex flex-col gap-2 flex-1">
                                            <div class="flex-1 flex items-center justify-center gap-2">
                                                <label for="paymentOptions" class="text-xs font-semibold font-weight-600 text-white uppercase">2. Payment Options</label>
                                                <p class="text-xs font-semibold text-echoes-primary">
                                                    {{ formatPrice(calculateTotalPrice) }}
                                                </p>
                                            </div>
                                            <div class="flex flex-col items-center bg-echoes-primary/10 rounded-lg p-2 text-sm font-medium text-gray-700 dark:text-gray-300" v-if="canUseCredits">
                                                <div>You have <span class="text-echoes-primary font-bold">{{ formatPrice(creditsBalance) }}</span> Credits</div>
                                                <div class="flex items-center gap-2">
                                                    <Checkbox v-model="useCredits" />
                                                    <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Use credits</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Buttons Row -->
                                    <div class="flex gap-3 items-center">
                                        <!-- Upload Section -->
                                        <div class="flex-1 space-y-2">
                                            <input 
                                                type="file" 
                                                ref="fileInputRef"
                                                accept=".json"
                                                @change="handleFileSelect"
                                                class="hidden"
                                            />
                                            <button 
                                                v-if="!uploadedFile"
                                                @click="triggerFileUpload"
                                                :disabled="uploadStatus === 'uploading'"
                                                class="w-full px-4 h-[86px] rounded-[16px] font-semibold text-sm text-white transition-all duration-200 flex items-center justify-center gap-2"
                                                :class="uploadStatus === 'uploading' 
                                                    ? 'bg-[#2D2D2D] cursor-not-allowed' 
                                                    : 'bg-[#2D2D2D] hover:bg-[#393939]/90'"
                                            >
                                                <svg width="24" height="24" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="29" cy="29" r="27.5" stroke="currentColor" stroke-width="3"/>
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M29 11C29.5401 11 30.0508 11.2455 30.3882 11.6672L37.4993 20.5561C38.1127 21.3228 37.9884 22.4415 37.2217 23.0549C36.455 23.6682 35.3362 23.5439 34.7229 22.7772L30.7778 17.8458L30.7778 30.5556C30.7778 31.5374 29.9818 32.3333 29 32.3333C28.0182 32.3333 27.2222 31.5374 27.2222 30.5556L27.2222 17.8458L23.2771 22.7772C22.6637 23.5439 21.545 23.6682 20.7783 23.0549C20.0116 22.4415 19.8873 21.3228 20.5007 20.5561L27.6118 11.6672C27.9492 11.2455 28.4599 11 29 11ZM23.6667 30.5556L23.6667 28.7778H16.5556C14.5919 28.7778 13 30.3697 13 32.3333V39.4444C13 41.4081 14.5919 43 16.5556 43H41.4444C43.4081 43 45 41.4081 45 39.4444V32.3333C45 30.3697 43.4081 28.7778 41.4444 28.7778H34.3333V30.5556C34.3333 33.5011 31.9455 35.8889 29 35.8889C26.0545 35.8889 23.6667 33.5011 23.6667 30.5556ZM37.8889 34.1111C36.907 34.1111 36.1111 34.907 36.1111 35.8889C36.1111 36.8707 36.907 37.6667 37.8889 37.6667H37.9067C38.8885 37.6667 39.6844 36.8707 39.6844 35.8889C39.6844 34.907 38.8885 34.1111 37.9067 34.1111H37.8889Z" fill="white"/>
                                                </svg>
                                                {{ uploadStatus === 'uploading' ? 'Uploading...' : 'Select File (max size 5MB)' }}
                                            </button>

                                            <!-- Upload Progress -->
                                            <div v-if="uploadStatus === 'uploading'" class="w-full bg-echoes-background-black rounded-full h-1.5">
                                                <div 
                                                    class="bg-echoes-primary h-1.5 rounded-full transition-all duration-300"
                                                    :style="{ width: uploadProgress + '%' }"
                                                ></div>
                                            </div>

                                            <!-- Upload Status -->
                                            <div v-if="uploadedFile" class="flex items-center gap-2 p-2 bg-echoes-background-black rounded-lg">
                                                <i v-if="uploadStatus === 'uploaded'" class="mdi mdi-check-circle text-green-500 text-lg"></i>
                                                <i v-else-if="uploadStatus === 'error'" class="mdi mdi-alert-circle text-red-500 text-lg"></i>
                                                <div class="flex-1">
                                                    <p class="text-xs font-medium text-white">{{ uploadedFile.name }}</p>
                                                    <p class="text-[10px] text-white/60">{{ (uploadedFile.size / 1024).toFixed(2) }} KB</p>
                                                </div>
                                                <div class="flex gap-2">
                                                    <div @click="removeUploadedFile" class="text-xs text-red-500 hover:text-red-600 transition-colors underline cursor-pointer">
                                                        Remove
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Arrow Separator -->
                                        <div class="flex items-center justify-center px-2">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-white">
                                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </div>

                                        <!-- Payment Buttons -->
                                        <div class="flex-1 flex gap-3"
                                            :class="!isFileUploaded && 'opacity-40 pointer-events-none'"
                                        >
                                        <div key="normal-payments" v-if="calculateTotalPrice > 0" class="flex-1 flex gap-3" :class="paymentMethods.length > 1 ? 'grid grid-cols-2' : 'flex'">
                                            <span v-if="paymentMethods.length === 0" class="font-medium py-4 text-center">No payment methods available for this listing. Please contact <a href="mailto:hello@portexai.com" class="text-echoes-primary">hello@portexai.com</a> so we can analyze this case.</span>
                                            <button
                                                v-for="payment in paymentMethods"
                                                :key="payment.name"
                                                @click="payment.clickAction"
                                                :disabled="!isFileUploaded || (payment.name === 'USDC' && isProcessingUSDCPayment) || (payment.name === 'Stripe' && payment.clickAction === handlePayWithStripe && isProcessingStripePayment)"
                                                class="flex-1 px-4 h-[86px] rounded-[16px] font-semibold text-sm text-white transition-all duration-200 flex items-center justify-center gap-2 bg-[#2D2D2D] hover:bg-[#393939]/90">
                                                <img v-if="payment.name === 'USDC' && !isProcessingUSDCPayment" :src="payment.logo" :alt="payment.name" class="w-8">
                                                <i v-else-if="payment.name === 'USDC' && isProcessingUSDCPayment" class="mdi mdi-loading animate-spin size-5"></i>
                                                <img v-else-if="payment.name === 'Stripe' && payment.clickAction !== handleConnectStripe && !isProcessingStripePayment" :src="payment.logo" :alt="payment.name" class="w-8">
                                                <i v-else-if="payment.name === 'Stripe' && payment.clickAction === handlePayWithStripe && isProcessingStripePayment" class="mdi mdi-loading animate-spin size-5"></i>
                                                <img v-else-if="payment.name === 'Stripe'" :src="payment.logo" :alt="payment.name" class="w-8">
                                                <span v-if="payment.name === 'USDC' && !isProcessingUSDCPayment">{{ payment.name }}</span>
                                                <span v-else-if="payment.name === 'Stripe' && payment.clickAction !== handleConnectStripe && !isProcessingStripePayment">{{ payment.name }}</span>
                                                <span v-else-if="payment.name === 'Stripe' && payment.clickAction === handleConnectStripe">{{ payment.payWith }}</span>
                                            </button>
                                        </div>

                                            <div v-else class="flex-1 flex gap-3">
                                                <!-- Credits Payment -->
                                                <button
                                                    @click="handlePaymentCredits"
                                                    :disabled="isProcessingCreditsPayment"
                                                    class="flex-1 px-4 h-[86px] rounded-[16px] font-semibold text-sm text-white transition-all duration-200 flex items-center justify-center gap-2 bg-[#2D2D2D] hover:bg-[#393939]/90">
                                                    <img v-if="!isProcessingCreditsPayment" alt="Portex Logo" :src="PortexLogo" class="w-8">
                                                    <i v-else class="mdi mdi-loading animate-spin size-5"></i>
                                                    <span v-if="!isProcessingCreditsPayment"> Pay with Credits ({{ formatPrice(calculateTotalPrice) }})</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Helper Text -->
                                    <p v-if="!isFileUploaded" class="text-[10px] text-center text-white/60">
                                        Please upload your eval response file to continue
                                    </p>
                                </div>
                            </div>
                            </template>
                            <template v-else-if="showStripePayment">
                            <div key="stripe" class="h-full max-h-[calc(85vh-150px)] overflow-y-auto overflow-x-hidden">
                                <CountryStep v-if="userCountry.country === null" @save="handleUserCountry"/>
                                <StripePayment
                                    v-else
                                    @back="goBackToMain"
                                    :payments="payments" 
                                    :listingId="props.evalListing.id"
                                    :evalResponseFile="uploadedFilePath"
                                    :version="selectedVersionNumber"
                                />
                            </div>
                            </template>
                            <template v-else>
                            <div class="h-full overflow-y-auto overflow-x-hidden">
                                <USDCPayment 
                                @back="goBackToMain" 
                                @payment-success="handleUSDCPaymentSuccess"
                                :paymentAmount="evalListing.eval_price" 
                                :buyActive="evalListing" 
                                :userInfo="userInfo" 
                                :creditsUsed="creditsUsed"
                                :listingId="evalListing.id" 
                                :isEvalRun="true"
                                :modelResponseFile="uploadedFilePath"
                                :version="selectedVersionNumber"
                                />
                            </div>
                            </template>
                            </div>

                        </Transition>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
    
    <!-- Close Confirmation Alert -->
    <Alert 
        :show="showCloseAlert"
        title="Leave checkout?"
        message="You’ve uploaded a file. Closing now will discard it and you’ll need to upload it again."
        type="warning"
        confirmText="Close & discard"
        cancelText="Keep working"
        @confirm="handleCloseConfirm"
        @close="handleCloseCancel"
        :showCloseBtn="false"
        dataTestId="eval-modal-close-alert"
    />
</template>

<style scoped lang="postcss">
@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Slide transition styles */
.slide-enter-active,
.slide-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-x: hidden !important;
}

.slide-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.slide-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: gray transparent;
}

.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 3px;
    transition: background-color 0.3s ease;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #7C3AED;
}

.payment-method-card {
    @apply border-2 border-gray-600/50 rounded-xl px-4 pt-2 pb-4 text-center hover:bg-echoes-border-light transition-all duration-200 space-y-2 shadow-sm shadow-black transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-100;
}
</style>